/* eslint-disable complexity */
import { useEffect, useRef } from 'react';

import { SyncControlDatasourceState, SyncResultType, useSyncContext } from '@futrli/predict';

/**
 * Hook that triggers a callback when a sync operation finishes,
 * reporting the result type: success, partial (unreconciled transactions), or failed.
 *
 * Watches `isSyncing` transitions from true -> false and determines the outcome
 * based on `datasourceState` and `financialConfig.unreconciled_transactions_count`.
 *
 * When syncing starts (false -> true), calls onSyncResult(null) to clear any previous result.
 *
 * The callback is suppressed when:
 * - `isDrawerOpen` is true (user is already viewing sync details in the drawer)
 */
export const useSyncResultEffect = (onSyncResult: (result: SyncResultType | null) => void) => {
  const { isSyncing, datasourceState, financialConfig, isDrawerOpen } = useSyncContext();
  const wasSyncing = useRef(false);
  const callbackRef = useRef(onSyncResult);
  callbackRef.current = onSyncResult;

  useEffect(() => {
    if (!wasSyncing.current && isSyncing) {
      callbackRef.current(null);
    }

    if (wasSyncing.current && !isSyncing) {
      if (isDrawerOpen) {
        wasSyncing.current = isSyncing;
        return;
      }

      if (datasourceState === SyncControlDatasourceState.SYNC_FAILED) {
        callbackRef.current('failed');
      } else if (datasourceState === SyncControlDatasourceState.SYNC_COMPLETE) {
        const unreconciledCount = financialConfig?.unreconciled_transactions_count;
        if (typeof unreconciledCount === 'number' && unreconciledCount > 0) {
          callbackRef.current('partial');
        } else {
          callbackRef.current('success');
        }
      }
    }
    wasSyncing.current = isSyncing;
  }, [isSyncing, datasourceState, financialConfig, isDrawerOpen]);
};
